/**
 * useChat Hook
 *
 * Manages all chat state: conversations, messages, streaming, voice recording.
 * Provides a clean interface for the ChatWidget component.
 */

import { useState, useCallback, useRef } from 'react';
import ChatService, {
  Conversation,
  ChatMessage,
  SSEEvent,
  PageContext,
} from '@/services/chat.service';

interface StreamingMessage {
  role: 'assistant';
  content: string;
  toolEvents: Array<{
    type: 'tool_start' | 'tool_result';
    tool: string;
    input?: Record<string, unknown>;
    result?: Record<string, unknown>;
  }>;
  isStreaming: boolean;
}

export interface UseChatReturn {
  conversations: Conversation[];
  messages: ChatMessage[];
  streamingMessage: StreamingMessage | null;
  activeConversationId: string | null;
  isStreaming: boolean;
  isTranscribing: boolean;
  isLoading: boolean;
  sendMessage: (content: string, pageContext?: PageContext) => Promise<void>;
  stopGeneration: () => void;
  transcribeAudio: (blob: Blob) => Promise<string>;
  createConversation: () => Promise<string>;
  switchConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  loadConversations: () => Promise<void>;
}

export function useChat(): UseChatReturn {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingMessage, setStreamingMessage] = useState<StreamingMessage | null>(null);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      const list = await ChatService.listConversations();
      setConversations(list);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  }, []);

  const switchConversation = useCallback(async (id: string) => {
    setIsLoading(true);
    try {
      const convo = await ChatService.getConversation(id);
      setActiveConversationId(id);
      setMessages(convo.messages);
      setStreamingMessage(null);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createConversation = useCallback(async (): Promise<string> => {
    const { id } = await ChatService.createConversation();
    setActiveConversationId(id);
    setMessages([]);
    setStreamingMessage(null);
    await loadConversations();
    return id;
  }, [loadConversations]);

  const deleteConversation = useCallback(async (id: string) => {
    await ChatService.deleteConversation(id);
    if (activeConversationId === id) {
      setActiveConversationId(null);
      setMessages([]);
    }
    await loadConversations();
  }, [activeConversationId, loadConversations]);

  const stopGeneration = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsStreaming(false);
    setStreamingMessage(prev => prev ? { ...prev, isStreaming: false } : prev);
  }, []);

  const sendMessage = useCallback(async (content: string, pageContext?: PageContext) => {
    let conversationId = activeConversationId;
    if (!conversationId) {
      conversationId = await createConversation();
    }

    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      toolName: null,
      toolInput: null,
      toolResult: null,
      metadata: null,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    setIsStreaming(true);
    const streaming: StreamingMessage = {
      role: 'assistant',
      content: '',
      toolEvents: [],
      isStreaming: true,
    };
    setStreamingMessage(streaming);

    abortRef.current = new AbortController();

    try {
      await ChatService.sendMessage(
        conversationId,
        content,
        (event: SSEEvent) => {
          setStreamingMessage(prev => {
            if (!prev) return prev;
            switch (event.type) {
              case 'token':
                return { ...prev, content: prev.content + (event.content || '') };
              case 'tool_start':
                return {
                  ...prev,
                  toolEvents: [
                    ...prev.toolEvents,
                    { type: 'tool_start', tool: event.tool || '', input: event.toolInput },
                  ],
                };
              case 'tool_result':
                return {
                  ...prev,
                  toolEvents: [
                    ...prev.toolEvents,
                    { type: 'tool_result', tool: event.tool || '', result: event.toolResult },
                  ],
                };
              case 'done':
                return { ...prev, isStreaming: false };
              case 'error':
                return {
                  ...prev,
                  content: prev.content + `\n\n**Error:** ${event.content || 'Something went wrong'}`,
                  isStreaming: false,
                };
              default:
                return prev;
            }
          });
        },
        abortRef.current.signal,
        pageContext
      );
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Send message error:', error);
        setStreamingMessage(prev =>
          prev ? { ...prev, content: prev.content + '\n\n**Error:** Failed to get response', isStreaming: false } : prev
        );
      }
    } finally {
      setIsStreaming(false);
      setStreamingMessage(prev => prev ? { ...prev, isStreaming: false } : prev);

      // Reload conversation to get persisted messages
      if (conversationId) {
        try {
          const convo = await ChatService.getConversation(conversationId);
          setMessages(convo.messages);
          setStreamingMessage(null);
        } catch {
          // keep streaming message as fallback
        }
      }
      await loadConversations();
    }
  }, [activeConversationId, createConversation, loadConversations]);

  const transcribeAudio = useCallback(async (blob: Blob): Promise<string> => {
    setIsTranscribing(true);
    try {
      const { text } = await ChatService.transcribeAudio(blob);
      return text;
    } finally {
      setIsTranscribing(false);
    }
  }, []);

  return {
    conversations,
    messages,
    streamingMessage,
    activeConversationId,
    isStreaming,
    isTranscribing,
    isLoading,
    sendMessage,
    stopGeneration,
    transcribeAudio,
    createConversation,
    switchConversation,
    deleteConversation,
    loadConversations,
  };
}
